import React, { useMemo } from 'react';

import { selectClassName, SELECT_BACKGROUND } from '../FormField';

import { Choice, Group, RadioChoice, Toggle } from './OptionControls';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import MultiSelectField from 'CommonComponents/MultiSelectField';

const TYPES = [
    { value: 'P', label: () => $t('Paid staff') },
    { value: 'C', label: () => $t('Consultants') },
    { value: 'S', label: () => $t('Secondees') },
    { value: 'V', label: () => $t('Volunteers') },
];

const BLOCKS = [
    { key: 'start_chart', label: () => $t('Positions by planned start month') },
    { key: 'status_table', label: () => $t('Position status table') },
    { key: 'overdue', label: () => $t('Overdue positions') },
    { key: 'recruitment', label: () => $t('By recruitment status') },
    { key: 'cost', label: () => $t('Cost and savings to date') },
];

const PERIOD_BLOCKS = [
    { key: 'last_period', label: () => $t('Last reporting period') },
    { key: 'forward_look', label: () => $t('Forward look') },
];

const OVERDUE_LIMITS = [5, 8, 12];

const HORIZONS = [30, 60, 90];

/**
 * The departments, or the cost centres grouped under their department, the
 * org chart exclusions pick from.
 */
const exclusionOptions = (costcentres, departments, groupBy) => {
    if (groupBy !== 'costcentre') {
        return departments.map((department) => ({ value: department.value, label: department.label }));
    }

    const groups = new Map();

    costcentres.forEach((costcentre) => {
        if (costcentre.departmentId === null || costcentre.departmentId === undefined) {
            return;
        }

        if (!groups.has(costcentre.departmentId)) {
            groups.set(costcentre.departmentId, { label: costcentre.departmentName || $t('No department'), options: [] });
        }

        groups.get(costcentre.departmentId).options.push({ value: costcentre.value, label: costcentre.label });
    });

    return [...groups.values()];
};

/**
 * The workforce section's options: what the pages count, how they group, and
 * which blocks and pages they include.
 */
const WorkforceOptions = ({ options, costcentres, departments, readOnly, onChange }) => {
    const exclusions = useMemo(() => exclusionOptions(costcentres, departments, options.group_by), [costcentres, departments, options.group_by]);

    const set = (changes) => onChange({ ...options, ...changes });

    const toggleType = (type) => {
        const types = options.types.includes(type) ? options.types.filter((item) => item !== type) : [...options.types, type];

        set({ types: TYPES.map((item) => item.value).filter((value) => types.includes(value)) });
    };

    return (
        <div className="tw-flex tw-flex-col tw-mt-3 tw-ps-4 tw-border-s-2 tw-border-charcoal-15">
            <Group title={$t('Workforce types')}>
                <div className="tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-2">
                    {TYPES.map((type) => (
                        <CheckboxInput
                            key={type.value}
                            label={type.label()}
                            checked={options.types.includes(type.value)}
                            disabled={readOnly}
                            onChange={() => toggleType(type.value)}
                        />
                    ))}
                </div>
                {!options.types.length && <div className="tw-text-red-500 tw-text-sm">{$t('Select at least one workforce type.')}</div>}
            </Group>

            <Group title={$t('Group by')}>
                <RadioChoice
                    name="mp-workforce-group-by"
                    label={$t('Group by')}
                    value={options.group_by}
                    choices={[
                        { value: 'department', label: $t('Department') },
                        { value: 'costcentre', label: $t('Cost centre') },
                    ]}
                    disabled={readOnly}
                    onChange={(groupBy) => set({ group_by: groupBy, org_chart_exclusions: [] })}
                />
            </Group>

            <Group title={$t('Position page')}>
                {BLOCKS.map((block) => (
                    <Toggle
                        key={block.key}
                        label={block.label()}
                        checked={!!options.blocks[block.key]}
                        disabled={readOnly}
                        onChange={() => set({ blocks: { ...options.blocks, [block.key]: !options.blocks[block.key] } })}
                    />
                ))}
                {options.blocks.overdue && (
                    <Choice
                        id="mp-workforce-overdue-limit"
                        label={$t('Overdue positions shown')}
                        value={options.overdue_limit}
                        choices={OVERDUE_LIMITS.map((limit) => ({ value: limit, label: $t('Top {{count}}', { count: limit }) }))}
                        disabled={readOnly}
                        onChange={(value) => set({ overdue_limit: value })}
                    />
                )}
            </Group>

            <Group title={$t('Period page')}>
                <Toggle
                    label={$t('Include the period page')}
                    checked={!!options.period_page}
                    disabled={readOnly}
                    onChange={() => set({ period_page: !options.period_page })}
                />
                {options.period_page &&
                    PERIOD_BLOCKS.map((block) => (
                        <Toggle
                            key={block.key}
                            label={block.label()}
                            checked={!!options.period_blocks[block.key]}
                            disabled={readOnly}
                            onChange={() => set({ period_blocks: { ...options.period_blocks, [block.key]: !options.period_blocks[block.key] } })}
                        />
                    ))}
                {options.period_page && options.period_blocks.forward_look && (
                    <Choice
                        id="mp-workforce-horizon"
                        label={$t('Forward look')}
                        value={options.horizon_days}
                        choices={HORIZONS.map((days) => ({ value: days, label: $t('{{count}} days', { count: days }) }))}
                        disabled={readOnly}
                        onChange={(value) => set({ horizon_days: value })}
                    />
                )}
            </Group>

            <Group title={$t('Appendix')}>
                <div className="tw-flex tw-flex-col tw-gap-1.5">
                    <label className="tw-flex tw-flex-col tw-mb-0" htmlFor="mp-workforce-exclusions">
                        <span className="tw-text-sm tw-text-charcoal">
                            {options.group_by === 'costcentre' ? $t('Exclude cost centres from the org charts') : $t('Exclude departments from the org charts')}
                        </span>
                        <span className="tw-text-xs tw-text-charcoal-60">{$t('Used by Appendix A6 organisation charts')}</span>
                    </label>
                    <MultiSelectField
                        inputId="mp-workforce-exclusions"
                        className={selectClassName}
                        backgroundColor={SELECT_BACKGROUND}
                        options={exclusions}
                        isGrouped={options.group_by === 'costcentre'}
                        hideSelectedOptions={false}
                        clearable
                        isDisabled={readOnly}
                        value={options.org_chart_exclusions}
                        placeholder={$t('None excluded')}
                        onChange={(selected) => set({ org_chart_exclusions: (selected || []).map((option) => option.value) })}
                    />
                </div>
            </Group>
        </div>
    );
};

export default WorkforceOptions;
